<template>
  <modal
    :show="internalShow"
    :centered="true"
    :closeOnOutsideClick="false"
    modalClasses="modal-lg"
    headerClasses="justify-content-center"
    @close="handleClose"
  >
    <template slot="header">
      <h4 class="title title-up">Price Change Notification</h4>
    </template>

    <div class="subscription-modal-body">
      <p class="subscription-intro">
        The pricing for the following Module(s) has been changed:
      </p>

      <!-- Module Card -->
      <div 
        v-for="(module, index) in modules" 
        :key="index" 
        class="module-card"
      >
        <div class="module-header">
          <div class="module-name-wrapper">
            <span class="module-name">
              {{ module.name }}
            </span>
          </div>
          <span class="module-hint">(Each Module should be listed individually with the info below for each)</span>
        </div>

        <!-- Module Details -->
        <div class="module-details">
          <div class="subscription-info-vertical">
            <div class="info-item-vertical">
              <strong>Monthly Fee :</strong> 
              <span v-if="priceChangeData.hasMinCostMonthChange" class="price-change">
                {{ formatCurrency(priceChangeData.prevMinCostMonth) }} → {{ formatCurrency(module.monthlyFee) }}
              </span>
              <span v-else>{{ formatCurrency(module.monthlyFee) }}</span>
            </div>
            <div class="info-item-vertical">
              <strong>Monthly Allocation :</strong> 
              <span v-if="priceChangeData.hasMaxCampaignsChange" class="price-change">
                {{ priceChangeData.prevMaxCampaigns }} campaigns → {{ module.monthlyAllocation }}
              </span>
              <span v-else>{{ module.monthlyAllocation }}</span>
            </div>
            <div class="info-item-vertical">
              <strong>Overage Billing :</strong> {{ module.overageBilling ? 'Enabled' : 'Disabled' }}
            </div>
            <div class="info-item-vertical">
              <strong>Overage Rate :</strong> {{ formatCurrency(module.overageRate) }}
            </div>
            <div class="info-item-vertical">
              <strong>Commitment Term :</strong> {{ module.commitmentTerm }} months
            </div>
            <div class="info-item-vertical">
              <strong>Billing Frequency :</strong> {{ module.billingFrequency }}
            </div>
            <div class="info-item-vertical">
              <strong>Effective Date :</strong> {{ formatDate(module.effectiveDate) }}
            </div>
            <div class="info-item-vertical">
              <strong>First Billing Date :</strong> {{ formatDate(module.firstBillingDate) }}
            </div>
            <div class="info-item-vertical">
              <strong>Annual Commitment Total :</strong> {{ formatCurrency(module.annualCommitmentTotal) }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="isAgencyCharge" class="calculations-section">
        <!-- Calculation Breakdown Section -->
        <el-divider></el-divider>
        <div class="calculations-card">
          <!-- <p class="calculation-title">Calculations</p> -->
          <p class="module-name mb-1">Calculations Cost</p>
          <div>
            <div>
              <small class="mr-2">1.</small>
              <small>The new price per campaign is <strong>${{ pricePerCampaign }}</strong></small>
            </div>
            <div>
              <small class="mr-2">2.</small>
              <small>Total of campaigns not yet paid for <strong>{{ campaignsUnpaid }}</strong></small>
            </div>
            <div>
              <small class="mr-2">3.</small>
              <small>Time remaining in the current billing period, ({{ startDateHuman }} - {{ nextBillingDateHuman }}, as of {{ nowDateHuman }}) : <strong>{{ remainingWeeks }} weeks {{ remainingRatioPercent }}%</strong></small>
            </div>
            <div>
              <div>
                <small class="ml-3">(${{ pricePerCampaign }} price per campaign x {{ campaignsUnpaid }} total campaigns un paid x {{ remainingRatioPercent }}% period) : <strong>{{ totalAmount }}</strong></small>
              </div>
              <div>
                <small class="ml-3">Your cost is <strong>${{ totalAmount }}</strong></small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <!-- Divider before agreement text -->
        <el-divider></el-divider>
        <p class="agreement-text">
          By clicking "Confirm" below, you acknowledge and agree to the new pricing.
          By clicking "Decline", the pricing will be reverted to the previous values.
        </p>
      </div>
    </div>

    <template slot="footer">
      <div class="modal-footer-content">
        <base-button
          simple
          @click.native="handleDecline"
          :disabled="internalIsLoading"
          class="footer-button-decline"
        >
          Decline
        </base-button>
        <base-button
          type="primary"
          @click.native="handleConfirm"
          :disabled="internalIsLoading"
          class="footer-button-confirm"
        >
          <i v-if="internalIsLoading" class="fas fa-spinner fa-spin mr-2"></i>
          Confirm
        </base-button>
      </div>
    </template>
  </modal>
</template>

<script>
import { Modal, BaseButton } from '@/components';
import { Divider } from 'element-ui';

export default {
  name: 'PredictPriceChangeModal',
  components: {
    Modal,
    BaseButton,
    [Divider.name]: Divider,
  },
  props: {
    modules: {
      type: Array,
      default: () => []
    },
    priceChangeData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      // Internal state untuk show (proxy untuk prop)
      internalShow: false,
      internalIsLoading: false,
      
      isAgencyCharge: false,
      pricePerCampaign: 0,
      campaignsUnpaid: 0,
      remainingRatioPercent: 0,
      remainingWeeks: 0,
      totalAmount: 0,
      startDateHuman: '',
      nextBillingDateHuman: '',
      nowDateHuman: '',
    };
  },
  watch: {
    priceChangeData: {
      handler(val) {
        const calculationPricingPredict = val && val.calculationPricingPredict || {};
        this.isAgencyCharge = calculationPricingPredict.IsAgencyCharge || false;
        this.pricePerCampaign = calculationPricingPredict.PricePerCampaign || 0;
        this.campaignsUnpaid = calculationPricingPredict.CampaignsUnpaid || 0;
        this.remainingRatioPercent = calculationPricingPredict.RemainingRatioPercent || 0;
        this.remainingWeeks = calculationPricingPredict.RemainingWeeks || 0;
        this.totalAmount = calculationPricingPredict.TotalAmount || 0;
        this.startDateHuman = calculationPricingPredict.StartDateHuman || '';
        this.nextBillingDateHuman = calculationPricingPredict.NextBillingDateHuman || '';
        this.nowDateHuman = calculationPricingPredict.NowDateHuman || '';
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    formatCurrency(value) {
      if (value === null || value === undefined) return 'N/A';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value);
    },
    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    handleClose() {
      // Handle close dari modal (klik X atau outside click)
      this.$emit('close');
    },
    handleDecline() {
      if (this.internalIsLoading) {
        return;
      }
      // Emit decline event
      this.$emit('decline');
    },
    handleConfirm() {
      if (this.internalIsLoading) {
        return;
      }
      // Emit confirm event
      this.$emit('confirm');
    }
  },
}
</script>

<style scoped>
.calculation-title {
  font-size: 14px;
  color: #525f7f;
}

.calculations-section {
  margin-top: 24px;
}

.calculations-card {
  border: 1px solid #5e72e4;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.subscription-modal-body {
  padding: 24px 32px;
  max-height: 70vh;
  overflow-y: auto;
}

.subscription-intro {
  font-size: 14px;
  color: #525f7f;
  margin-bottom: 20px;
  line-height: 1.5;
}

.module-card {
  border: 1px solid #5e72e4;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  background-color: #ffffff;
  transition: all 0.2s ease;
}

.module-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.module-name-wrapper {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.module-name {
  font-size: 16px;
  font-weight: 600;
  color: #32325d;
  margin: 0;
}

.module-hint {
  font-size: 12px;
  color: #8898aa;
  margin-top: 4px;
}

.module-details {
  margin-top: 16px;
}

.subscription-info-vertical {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-item-vertical {
  font-size: 14px;
  color: #525f7f;
  line-height: 1.6;
}

.info-item-vertical strong {
  color: #32325d;
  margin-right: 8px;
}

.price-change {
  color: #dc3545;
  font-weight: 600;
}

.agreement-text {
  font-size: 13px;
  color: #525f7f;
  line-height: 1.6;
  margin: 0;
}

.modal-lg {
  max-width: 900px;
}

/* Footer Styles */
::v-deep .modal-footer {
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
  padding: 20px 32px;
  margin-top: 0;
}

.modal-footer-content {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 0;
  width: 100%;
}

.footer-button-decline {
  min-width: 100px;
  padding: 10px 24px;
  font-weight: 500;
  background-color: #ffffff;
  color: #525f7f;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

.footer-button-cancel:hover {
  background-color: #f7fafc;
  border-color: #cbd5e0;
  color: #32325d;
}

.footer-button-confirm {
  min-width: 160px;
  padding: 10px 24px;
  font-weight: 600;
  background-color: #212529;
  color: #ffffff;
  border: 1px solid #212529;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.footer-button-confirm:hover:not(:disabled) {
  background-color: #32325d;
  border-color: #32325d;
}

.footer-button-confirm:disabled {
  background-color: #cbd5e0;
  border-color: #cbd5e0;
  color: #8898aa;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Custom scrollbar */
.subscription-modal-body::-webkit-scrollbar {
  width: 6px;
}

.subscription-modal-body::-webkit-scrollbar-track {
  background: #f1f3f5;
  border-radius: 3px;
}

.subscription-modal-body::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.subscription-modal-body::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}
</style>

